/**
 * Settings ▸ Billing (admin): products, the house rate card, and the cost book.
 *
 * PRODUCTS: who may see and get each one. Nobody is let in by default — an
 * admin grants a private product to themselves like anyone else, which is
 * also how they check the block works for everyone who was not granted.
 *
 * THE RATE CARD is what customers pay: credits per 1k tokens by model tier,
 * credits per network call by kind, and the fraction charged when their own
 * key or network did the work. Saving makes a new VERSION; nothing is edited
 * in place, because every ledger row stamps the version it was priced at.
 *
 * THE COST BOOK is what the same work costs us. The LLM side lives on each
 * model (Settings ▸ AI ▸ Models: $ per million, and the provider's cost
 * basis); the network side is here: dollars per call by kind and downloader.
 *
 * Which model runs each stage for customers is the house PIPELINE, edited
 * under Settings ▸ AI ▸ Pipeline as versions (draft → test → promote).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import StripePrices from '../components/admin/StripePrices';
import { useEntitlements } from '../contexts/EntitlementContext';
import { PRODUCT_INFO } from '../shell/products';
import type {
  AdminCatalogRow,
  AdminNetworkCosts,
  AdminPriceBooks,
  LLMRate,
  ProductAvailability,
} from '../types';

const AVAILABILITY: { key: ProductAvailability; label: string; hint: string }[] = [
  { key: 'PUBLIC', label: 'Public', hint: 'On the launcher for everyone; free tier self-serves.' },
  { key: 'PRIVATE', label: 'Private', hint: 'Seen only by people you grant it to - yourself included.' },
  { key: 'SUNSETTING', label: 'Sunsetting', hint: 'Holders keep it with a retiring banner; no new users.' },
  { key: 'RETIRED', label: 'Retired', hint: 'Nobody enters. Artifacts stay on the shelf.' },
];

const KIND_LABEL: Record<string, string> = {
  LOOKUP_SERP: 'Search (SERP)',
  LOOKUP_PAGE: 'Page read',
  LOOKUP_DEMAND: 'Keyword demand (Google Ads)',
  LOOKUP_TRENDS: 'Trends',
};

const TIER_LABEL: Record<string, string> = { economy: 'Economy', standard: 'Standard', premium: 'Premium' };

type RateForm = Record<string, Record<keyof LLMRate, string>>;

function rateForm(rates: Record<string, LLMRate>, tiers: string[]): RateForm {
  const out: RateForm = {};
  for (const t of tiers) {
    const r = rates[t];
    out[t] = {
      input_per_1k: String(r?.input_per_1k ?? 0),
      output_per_1k: String(r?.output_per_1k ?? 0),
      cached_input_per_1k: String(r?.cached_input_per_1k ?? 0),
    };
  }
  return out;
}

const NUM = 'w-24 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2 py-1 text-sm text-right tabular-nums';

export default function AdminBilling() {
  // The plan table comes from the same place the customer's page reads it, so
  // the price picker cannot offer a plan the server does not have.
  const { plans } = useEntitlements();
  const [books, setBooks] = useState<AdminPriceBooks | null>(null);
  const [usd, setUsd] = useState('0.01');
  const [rates, setRates] = useState<RateForm>({});
  const [network, setNetwork] = useState<Record<string, string>>({});
  const [byokLlm, setByokLlm] = useState('0.3');
  const [byokNet, setByokNet] = useState('0.3');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');
  const [error, setError] = useState('');
  const [catalog, setCatalog] = useState<AdminCatalogRow[]>([]);
  const [costs, setCosts] = useState<AdminNetworkCosts | null>(null);
  const [costDraft, setCostDraft] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    try {
      const [pb, cat, nc] = await Promise.all([
        api.adminGetPriceBook(),
        api.adminGetCatalog(),
        api.adminGetNetworkCosts(),
      ]);
      setBooks(pb);
      setCatalog(cat);
      setCosts(nc);
      setCostDraft(Object.fromEntries(Object.entries(nc.costs).map(([k, v]) => [k, String(v)])));
      setUsd(String(pb.active.usd_per_credit));
      setRates(rateForm(pb.active.llm_rates, pb.tiers));
      const n: Record<string, string> = {};
      for (const k of pb.network_kinds) n[k] = String(pb.active.network_rates[k] ?? pb.defaults.network_rates[k] ?? 0);
      setNetwork(n);
      setByokLlm(String(pb.active.byok_llm_multiplier));
      setByokNet(String(pb.active.byok_network_multiplier));
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load billing settings');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    setSaved('');
    try {
      const llm_rates: Record<string, LLMRate> = {};
      for (const [t, r] of Object.entries(rates)) {
        llm_rates[t] = {
          input_per_1k: Number(r.input_per_1k),
          output_per_1k: Number(r.output_per_1k),
          cached_input_per_1k: Number(r.cached_input_per_1k),
        };
      }
      const network_rates: Record<string, number> = {};
      for (const [k, v] of Object.entries(network)) network_rates[k] = Number(v);
      const row = await api.adminSavePriceBook({
        usd_per_credit: Number(usd),
        llm_rates,
        network_rates,
        byok_llm_multiplier: Number(byokLlm),
        byok_network_multiplier: Number(byokNet),
        note: note.trim() || undefined,
      });
      setSaved(`Saved as version ${row.version}.`);
      setNote('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const saveCost = async (kind: string, downloader: string) => {
    const key = `network_cost.${kind}.${downloader}`;
    try {
      await api.adminPutNetworkCost(kind, downloader, Number(costDraft[key] ?? 0));
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the cost');
    }
  };

  const setAvailability = async (row: AdminCatalogRow, patch: Partial<AdminCatalogRow>) => {
    try {
      const next = { ...row, ...patch };
      await api.adminPutCatalog(row.product, {
        availability: next.availability,
        sunset_at: next.sunset_at || null,
        note: next.note || null,
      });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update the product');
    }
  };

  return (
    <div className="space-y-10">
      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

      <section>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Products</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Who may see and get each product. Nobody is let in by default - to see a private product yourself,
          grant it to yourself under Users, which is also how you check the block works for everyone else.
        </p>
        <table className="mt-3 w-full text-sm">
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {catalog.map((row) => (
              <tr key={row.product}>
                <td className="py-2 pr-4 text-gray-800 dark:text-gray-200 whitespace-nowrap">
                  {PRODUCT_INFO[row.product]?.name ?? row.product}
                </td>
                <td className="py-2 pr-4">
                  <select
                    aria-label={`${row.product} availability`}
                    value={row.availability}
                    onChange={(e) => setAvailability(row, { availability: e.target.value as ProductAvailability })}
                    className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2 py-1 text-sm"
                  >
                    {AVAILABILITY.map((a) => (
                      <option key={a.key} value={a.key}>
                        {a.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="py-2 pr-4">
                  {row.availability === 'SUNSETTING' && (
                    <input
                      type="date"
                      aria-label={`${row.product} sunset date`}
                      value={row.sunset_at ? row.sunset_at.slice(0, 10) : ''}
                      onChange={(e) => setAvailability(row, { sunset_at: e.target.value || null })}
                      className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2 py-1 text-sm"
                    />
                  )}
                </td>
                <td className="py-2 text-xs text-gray-500 dark:text-gray-400">
                  {AVAILABILITY.find((a) => a.key === row.availability)?.hint}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Rate card - what customers pay</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Credits per 1k tokens by model tier, and per call by lookup kind. Stable and yours: swapping a model under
          a stage never changes a customer's bill. Saving makes a new version; charges already made keep the version
          they were priced at.
          {books && ` Active: version ${books.active.version}${books.active.version === 0 ? ' (built-in defaults - nothing saved yet)' : ''}.`}
        </p>
        {books && (
          <div className="mt-4 grid gap-6 md:grid-cols-2">
            <div className="space-y-4">
              <label className="flex items-center justify-between gap-3 text-sm">
                <span className="text-gray-700 dark:text-gray-200">Dollars per credit</span>
                <input type="number" step="0.001" min="0" value={usd} onChange={(e) => setUsd(e.target.value)} className={NUM} />
              </label>

              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 dark:text-gray-400">
                    <th className="text-left font-medium py-1">Tier · credits per 1k</th>
                    <th className="text-right font-medium py-1">input</th>
                    <th className="text-right font-medium py-1">cached in</th>
                    <th className="text-right font-medium py-1">output</th>
                  </tr>
                </thead>
                <tbody>
                  {books.tiers.map((t) => (
                    <tr key={t}>
                      <td className="py-1 text-gray-700 dark:text-gray-200">{TIER_LABEL[t] ?? t}</td>
                      {(['input_per_1k', 'cached_input_per_1k', 'output_per_1k'] as (keyof LLMRate)[]).map((f) => (
                        <td key={f} className="py-1 text-right">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            aria-label={`${t} ${f}`}
                            value={rates[t]?.[f] ?? ''}
                            onChange={(e) => setRates((r) => ({ ...r, [t]: { ...r[t], [f]: e.target.value } }))}
                            className={NUM}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="space-y-1">
                {books.network_kinds.map((k) => (
                  <label key={k} className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-gray-700 dark:text-gray-200">{KIND_LABEL[k] ?? k} · credits per call</span>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      aria-label={`${KIND_LABEL[k] ?? k} credits`}
                      value={network[k] ?? ''}
                      onChange={(e) => setNetwork((n) => ({ ...n, [k]: e.target.value }))}
                      className={NUM}
                    />
                  </label>
                ))}
              </div>

              <div className="space-y-1">
                <label className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-gray-700 dark:text-gray-200" title="Fraction of the house rate charged when the customer's own key ran the model">
                    Own-key multiplier
                  </span>
                  <input type="number" step="0.05" min="0" max="1" value={byokLlm} onChange={(e) => setByokLlm(e.target.value)} className={NUM} />
                </label>
                <label className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-gray-700 dark:text-gray-200" title="Fraction charged when the customer's own crawler, key or browser did the fetch">
                    Own-network multiplier
                  </span>
                  <input type="number" step="0.05" min="0" max="1" value={byokNet} onChange={(e) => setByokNet(e.target.value)} className={NUM} />
                </label>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="why this change (optional)"
                  className="flex-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2 py-1 text-sm"
                />
                <button
                  type="button"
                  disabled={saving}
                  onClick={save}
                  className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  Save as new version
                </button>
              </div>
              {saved && <p className="text-xs text-emerald-700 dark:text-emerald-300">{saved}</p>}
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Versions</h4>
              {books.versions.length === 0 ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">None saved yet.</p>
              ) : (
                <ul className="text-xs text-gray-600 dark:text-gray-300 space-y-1 max-h-72 overflow-y-auto">
                  {books.versions.map((v) => (
                    <li key={v.version} className={v.active ? 'font-medium text-gray-900 dark:text-gray-100' : ''}>
                      v{v.version} · ${v.usd_per_credit}/credit · standard {v.llm_rates.standard?.input_per_1k ?? '?'}/
                      {v.llm_rates.standard?.output_per_1k ?? '?'} per 1k · search {v.network_rates.LOOKUP_SERP ?? '?'} · own key ×
                      {v.byok_llm_multiplier}
                      {v.note ? ` - ${v.note}` : ''}
                      {v.active ? ' (active)' : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </section>

      <section>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Cost book - what it costs us</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Never shown to a customer. AI cost is on each model (Settings ▸ AI ▸ Models: dollars per million, and the
          provider's cost basis - metered, flat plan, or self-hosted). Network cost is here: dollars per call by lookup
          kind and downloader. Unset pairs count as free, which overstates margin - set the ones you pay for.
        </p>
        {costs && (
          <div className="mt-3 overflow-x-auto">
            <table className="text-sm">
              <thead>
                <tr className="text-xs text-gray-500 dark:text-gray-400">
                  <th className="text-left font-medium py-1 pr-4">$ per call</th>
                  {costs.downloaders.map((d) => (
                    <th key={d} className="text-right font-medium py-1 px-2">
                      {d}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {costs.kinds.map((k) => (
                  <tr key={k}>
                    <td className="py-1 pr-4 text-gray-700 dark:text-gray-200 whitespace-nowrap">{KIND_LABEL[k] ?? k}</td>
                    {costs.downloaders.map((d) => {
                      const key = `network_cost.${k}.${d}`;
                      return (
                        <td key={d} className="py-1 px-2 text-right">
                          <input
                            type="number"
                            step="0.0001"
                            min="0"
                            aria-label={`${k} ${d} cost`}
                            value={costDraft[key] ?? ''}
                            placeholder="0"
                            onChange={(e) => setCostDraft((c) => ({ ...c, [key]: e.target.value }))}
                            onBlur={() => {
                              if ((costDraft[key] ?? '') !== String(costs.costs[key] ?? '')) void saveCost(k, d);
                            }}
                            className={NUM}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <StripePrices plans={plans} />

      <section>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Model policy</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Which models may do each job for customers is a versioned release, edited under{' '}
          <Link to="/settings?tab=ai&sub=policy" className="text-blue-600 dark:text-blue-400 hover:underline">
            Settings ▸ AI ▸ Model policy
          </Link>
          : make a draft, test it, promote it.
        </p>
      </section>
    </div>
  );
}
